import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  Input,
  Text,
  Card,
  Badge,
  Modal,
  SlideOver,
} from '@teals/ui-web';

interface Partner {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'suspended' | 'retired';
  apiKey: string;
  budgetUsed: number;
  budgetCap: number;
  perAgentCap: number;
  associatedAgents: { id: string; name: string; enabled: boolean }[];
}

const MOCK_PARTNERS: Partner[] = [
  {
    id: 'ptnr_8f3a21c9e7b4',
    name: 'Acme Corp',
    description: 'Enterprise integration partner for HR automation workflows.',
    status: 'active',
    apiKey: 'lk_live_a8f3b21c9e7b4d5f6a2e1c8d',
    budgetUsed: 7200,
    budgetCap: 10000,
    perAgentCap: 2500,
    associatedAgents: [
      { id: 'ag-1', name: 'Resume Screener', enabled: true },
      { id: 'ag-2', name: 'Interview Scheduler', enabled: true },
      { id: 'ag-3', name: 'Onboarding Assistant', enabled: false },
    ],
  },
  {
    id: 'ptnr_b4d5f6a2e1c8',
    name: 'TechFlow Inc',
    description: 'API consumer for internal knowledge base agent.',
    status: 'active',
    apiKey: 'lk_live_b4d5f6a2e1c8d9e7a3f2b1c4',
    budgetUsed: 3400,
    budgetCap: 8000,
    perAgentCap: 4000,
    associatedAgents: [
      { id: 'ag-4', name: 'Knowledge Base Agent', enabled: true },
      { id: 'ag-5', name: 'Support Triage Bot', enabled: true },
    ],
  },
  {
    id: 'ptnr_c9e7b4d5f6a2',
    name: 'GlobalRetail',
    description: 'Suspended due to billing review. Pending reactivation.',
    status: 'suspended',
    apiKey: 'lk_live_c9e7b4d5f6a2e1c8d3f9b7a6',
    budgetUsed: 9800,
    budgetCap: 10000,
    perAgentCap: 5000,
    associatedAgents: [
      { id: 'ag-6', name: 'Product Recommender', enabled: false },
    ],
  },
  {
    id: 'ptnr_d5f6a2e1c8d9',
    name: 'FinanceHub Ltd',
    description: 'Retired partner from pilot programme. Read-only access.',
    status: 'retired',
    apiKey: 'lk_live_d5f6a2e1c8d9e7b4a3f2c1b5',
    budgetUsed: 1200,
    budgetCap: 5000,
    perAgentCap: 2500,
    associatedAgents: [
      { id: 'ag-7', name: 'Compliance Checker', enabled: false },
      { id: 'ag-8', name: 'Report Generator', enabled: false },
    ],
  },
];

const STATUS_MAP: Record<
  Partner['status'],
  { label: string; variant: 'success' | 'warning' | 'neutral' }
> = {
  active: { label: 'Active', variant: 'success' },
  suspended: { label: 'Suspended', variant: 'warning' },
  retired: { label: 'Retired', variant: 'neutral' },
};

const PageWrapper = styled.div`
  padding: 2rem;
  max-width: 1280px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: 1023px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const PartnerCard = styled(Card)`
  cursor: pointer;
  border: 1px solid var(--border);
  transition: box-shadow 0.2s, border-color 0.2s;

  &:hover {
    border-color: var(--accent);
    box-shadow: var(--shadow-md);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const PartnerId = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0;
  border: none;
  background: none;
  color: var(--text-muted);
  font-size: 0.75rem;
  font-family: monospace;
  cursor: pointer;

  &:hover {
    color: var(--accent);
  }
`;

const ProgressBarOuter = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: var(--bg-inset);
  margin-top: 0.75rem;
  overflow: hidden;
`;

const ProgressBarInner = styled.div<{ $pct: number; $warn: boolean }>`
  height: 100%;
  border-radius: 3px;
  width: ${({ $pct }) => Math.min($pct, 100)}%;
  background: ${({ $warn }) => ($warn ? 'var(--warning)' : 'var(--accent)')};
  transition: width 0.3s ease;
`;

const BudgetLabel = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const ModalForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const DetailSection = styled.div`
  margin-bottom: 1.5rem;
`;

const DetailLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.375rem;
`;

const DetailValue = styled.div`
  font-size: 0.875rem;
  color: var(--text-primary);
  word-break: break-all;
`;

const KeyDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: var(--bg-inset);
  border-radius: 8px;
  font-family: monospace;
  font-size: 0.8125rem;
  color: var(--text-primary);
`;

const KeyActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const AgentCheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border);
  font-size: 0.875rem;
  color: var(--text-primary);
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }
`;

const BudgetFieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`;

const StatusActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--border);
  margin: 1.5rem 0;
`;

const Partners: React.FC = () => {
  const [partners, setPartners] = useState<Partner[]>(MOCK_PARTNERS);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [keyVisible, setKeyVisible] = useState(false);

  const copyToClipboard = (text: string, id?: string) => {
    navigator.clipboard.writeText(text);
    if (id) {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    }
  };

  const handleCreate = () => {
    if (!newName.trim()) return;
    const partner: Partner = {
      id: `ptnr_${Math.random().toString(36).slice(2, 14)}`,
      name: newName,
      description: newDesc,
      status: 'active',
      apiKey: `lk_live_${Math.random().toString(36).slice(2, 26)}`,
      budgetUsed: 0,
      budgetCap: 5000,
      perAgentCap: 2500,
      associatedAgents: [],
    };
    setPartners((prev) => [...prev, partner]);
    setNewName('');
    setNewDesc('');
    setCreateOpen(false);
  };

  const toggleStatus = (partnerId: string) => {
    setPartners((prev) =>
      prev.map((p) => {
        if (p.id !== partnerId) return p;
        const next = p.status === 'active' ? 'suspended' : 'active';
        const updated = { ...p, status: next as Partner['status'] };
        if (selectedPartner?.id === partnerId) setSelectedPartner(updated);
        return updated;
      }),
    );
  };

  const truncateId = (id: string) => {
    if (id.length <= 14) return id;
    return `${id.slice(0, 10)}…${id.slice(-4)}`;
  };

  const maskKey = (key: string) =>
    `${key.slice(0, 8)}${'•'.repeat(16)}${key.slice(-4)}`;

  return (
    <PageWrapper>
      <Header>
        <Text variant="h2">Partner Accounts</Text>
        <Button variant="primary" onClick={() => setCreateOpen(true)}>
          Create Partner
        </Button>
      </Header>

      <Grid>
        {partners.map((partner) => {
          const pct = (partner.budgetUsed / partner.budgetCap) * 100;
          const { label, variant } = STATUS_MAP[partner.status];
          return (
            <PartnerCard
              key={partner.id}
              padding={20}
              elevation="sm"
              onClick={() => {
                setSelectedPartner(partner);
                setKeyVisible(false);
              }}
            >
              <CardHeader>
                <Text variant="h4">{partner.name}</Text>
                <Badge variant={variant} size="sm">
                  {label}
                </Badge>
              </CardHeader>
              <PartnerId
                onClick={(e) => {
                  e.stopPropagation();
                  copyToClipboard(partner.id, partner.id);
                }}
                title="Click to copy"
              >
                {truncateId(partner.id)}
                <span>{copiedId === partner.id ? '✓' : '📋'}</span>
              </PartnerId>
              <ProgressBarOuter>
                <ProgressBarInner $pct={pct} $warn={pct >= 90} />
              </ProgressBarOuter>
              <BudgetLabel>
                <span>${partner.budgetUsed.toLocaleString()} used</span>
                <span>${partner.budgetCap.toLocaleString()} cap</span>
              </BudgetLabel>
            </PartnerCard>
          );
        })}
      </Grid>

      <Modal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create Partner"
      >
        <ModalForm>
          <Input
            label="Partner name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Acme Corp"
          />
          <Input
            label="Description"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            placeholder="Brief description of the partner"
          />
          <ModalActions>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreate}>
              Create
            </Button>
          </ModalActions>
        </ModalForm>
      </Modal>

      <SlideOver
        isOpen={!!selectedPartner}
        onClose={() => setSelectedPartner(null)}
        title={selectedPartner?.name ?? ''}
        width="lg"
        position="right"
      >
        {selectedPartner && (
          <>
            <DetailSection>
              <DetailLabel>Partner ID</DetailLabel>
              <DetailValue style={{ fontFamily: 'monospace' }}>
                {selectedPartner.id}
              </DetailValue>
            </DetailSection>

            <DetailSection>
              <DetailLabel>Description</DetailLabel>
              <DetailValue>{selectedPartner.description}</DetailValue>
            </DetailSection>

            <DetailSection>
              <DetailLabel>Status</DetailLabel>
              <Badge
                variant={STATUS_MAP[selectedPartner.status].variant}
                size="md"
              >
                {STATUS_MAP[selectedPartner.status].label}
              </Badge>
              {selectedPartner.status !== 'retired' && (
                <StatusActions>
                  <Button
                    variant={
                      selectedPartner.status === 'active'
                        ? 'secondary'
                        : 'primary'
                    }
                    onClick={() => toggleStatus(selectedPartner.id)}
                  >
                    {selectedPartner.status === 'active'
                      ? 'Suspend Partner'
                      : 'Reactivate Partner'}
                  </Button>
                </StatusActions>
              )}
            </DetailSection>

            <Divider />

            <DetailSection>
              <DetailLabel>Partner API Key</DetailLabel>
              <KeyDisplay>
                <span style={{ flex: 1 }}>
                  {keyVisible
                    ? selectedPartner.apiKey
                    : maskKey(selectedPartner.apiKey)}
                </span>
                <Button
                  variant="ghost"
                  onClick={() => setKeyVisible(!keyVisible)}
                >
                  {keyVisible ? 'Hide' : 'Show'}
                </Button>
              </KeyDisplay>
              <KeyActions>
                <Button
                  variant="secondary"
                  onClick={() =>
                    copyToClipboard(selectedPartner.apiKey, 'key')
                  }
                >
                  {copiedId === 'key' ? 'Copied!' : 'Copy Key'}
                </Button>
                <Button variant="ghost">Regenerate</Button>
              </KeyActions>
            </DetailSection>

            <Divider />

            <DetailSection>
              <DetailLabel>Associated Agents</DetailLabel>
              {selectedPartner.associatedAgents.length === 0 ? (
                <DetailValue style={{ color: 'var(--text-muted)' }}>
                  No agents associated
                </DetailValue>
              ) : (
                selectedPartner.associatedAgents.map((agent) => (
                  <AgentCheckRow key={agent.id}>
                    <input
                      type="checkbox"
                      checked={agent.enabled}
                      readOnly
                    />
                    {agent.name}
                  </AgentCheckRow>
                ))
              )}
            </DetailSection>

            <Divider />

            <DetailSection>
              <DetailLabel>Budget Configuration</DetailLabel>
              <BudgetFieldRow>
                <Input
                  label="Per-agent cap ($)"
                  value={String(selectedPartner.perAgentCap)}
                  onChange={() => {}}
                />
                <Input
                  label="Org-wide cap ($)"
                  value={String(selectedPartner.budgetCap)}
                  onChange={() => {}}
                />
              </BudgetFieldRow>
              <div style={{ marginTop: '0.75rem' }}>
                <ProgressBarOuter>
                  <ProgressBarInner
                    $pct={
                      (selectedPartner.budgetUsed /
                        selectedPartner.budgetCap) *
                      100
                    }
                    $warn={
                      selectedPartner.budgetUsed / selectedPartner.budgetCap >=
                      0.9
                    }
                  />
                </ProgressBarOuter>
                <BudgetLabel>
                  <span>
                    ${selectedPartner.budgetUsed.toLocaleString()} used
                  </span>
                  <span>
                    ${selectedPartner.budgetCap.toLocaleString()} cap
                  </span>
                </BudgetLabel>
              </div>
            </DetailSection>
          </>
        )}
      </SlideOver>
    </PageWrapper>
  );
};

export default Partners;
